/*
 * US phone display formatting: 8599635663 → 859-963-5663.
 *
 * Shared by the admin portal and the CRM. It lives in a package rather than in each app
 * because the two rules below are not obvious, were both found by driving a real input, and
 * a second copy would only be fixed once.
 *
 * The column is free text and stays that way — this formats what a founder types and what
 * is shown, it does not validate. A tenant whose number does not fit the US shape still
 * needs to be reachable, so anything that is not plainly a US number is returned untouched
 * rather than mangled into one.
 *
 * ⚠️ NO TRAILING SEPARATOR IS EVER EMITTED. Returning "859-" after three digits is what makes
 * as-you-type formatters fight backspace: the user deletes the dash, the formatter puts it
 * straight back, and the caret never moves. Because a group's separator only appears once the
 * next digit exists, deleting always makes progress.
 */

/**
 * Left alone: international (+…), extensions, letters, or a digit count that is not a US
 * number.
 *
 * ⚠️ 11 DIGITS NOT STARTING WITH 1 MUST BE LEFT ALONE, not squeezed into the 10-digit shape.
 * Formatting it emits only the first ten and the eleventh vanishes — typing an extra digit
 * into a complete number looked like the keystroke had simply not registered, with no way to
 * tell that the field had eaten it.
 */
function isUnformattable(raw: string, digits: string): boolean {
  if (raw.trim().startsWith('+') || /[a-zA-Z]/.test(raw)) return true;
  if (digits.length > 11) return true;
  return digits.length === 11 && !digits.startsWith('1');
}

/**
 * As-you-type formatting, for an input's onChange. Formats partial input progressively so
 * the separators appear while the number is being typed.
 */
export function formatPhoneInput(raw: string): string {
  if (!raw) return raw;
  const digits = raw.replace(/\D/g, '');
  if (isUnformattable(raw, digits)) return raw;

  // A leading 1 is the country code, kept as its own group: 1-859-963-5663.
  const hasCountry = digits.length === 11 && digits.startsWith('1');
  const country = hasCountry ? '1-' : '';
  const d = hasCountry ? digits.slice(1) : digits;

  if (d.length <= 3) return country + d;
  if (d.length <= 6) return `${country}${d.slice(0, 3)}-${d.slice(3)}`;
  return `${country}${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

/**
 * Display formatting for a value already stored.
 *
 * ⚠️ ONLY A COMPLETE US NUMBER IS TOUCHED — 10 digits, or 11 beginning with 1. Partial
 * formatting is right while someone types and WRONG for stored data: applied to the seeded
 * 7-digit `555-0100` it produced `555-010-0`, inventing a grouping the number does not have
 * and making an existing contact look corrupted. Anything else is returned exactly as
 * entered, which is also what an operator who typed something unusual expects to see.
 */
export function formatPhone(raw: string): string {
  if (!raw) return raw;
  const digits = raw.replace(/\D/g, '');
  const complete = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  if (!complete || isUnformattable(raw, digits)) return raw;
  return formatPhoneInput(raw);
}
